import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';

function TestScreen() {
    const { user, token, logout } = useContext(UserContext); // Get user, token, and logout from context
    const navigate = useNavigate();

    // State for the current test data
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionIndex, setQuestionIndex] = useState(0); // Tracks current question being displayed
    const [selectedAnswers, setSelectedAnswers] = useState([]); // Stores selected answers for current question
    const [message, setMessage] = useState(''); // General messages (loading, error, etc.)
    const [isLoading, setIsLoading] = useState(true);
    const [testCompleted, setTestCompleted] = useState(false);

    // Replace with your actual Lambda API Gateway URL
    const LAMBDA_API_URL = 'https://0u1laix2cb.execute-api.us-east-1.amazonaws.com/Prod/TestContent'; // EXAMPLE URL, REPLACE THIS

    useEffect(() => {
        // Redirect if not authenticated
        if (!user || !token) {
            navigate('/');
            return;
        }

        const fetchQuestion = async () => {
            setIsLoading(true);
            setMessage('Loading question...');
            try {
                const response = await fetch(LAMBDA_API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        testId: "default-test", // Hardcoded test ID for this example
                        questionIndex: questionIndex
                    })
                });

                const data = await response.json();

                if (response.ok) {
                    if (data.isEndOfTest) {
                        setTestCompleted(true);
                        setMessage("Test completed! Calculating score...");
                        // TODO: Implement actual score calculation and display here
                        // You would typically send all collected answers to another Lambda endpoint for scoring.
                    } else {
                        setCurrentQuestion(data);
                        setSelectedAnswers([]); // Reset selections for the new question
                        setMessage('');
                    }
                } else {
                    setMessage(`Error loading question: ${data.message || 'Unknown error'}`);
                    setCurrentQuestion(null); // Clear question on error
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setMessage(`Failed to fetch question: ${error.message}`);
                setCurrentQuestion(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestion();
    }, [questionIndex, user, token, navigate, LAMBDA_API_URL]); // Re-fetch when questionIndex changes or user/token status changes

    // Handle single-choice (radio button) selection
    const handleSingleChoiceChange = (event) => {
        setSelectedAnswers([event.target.value]);
    };

    // Handle multiple-choice (checkbox) selection
    const handleMultipleChoiceChange = (event) => {
        const { value, checked } = event.target;
        setSelectedAnswers(prev =>
            checked ? [...prev, value] : prev.filter(answerId => answerId !== value)
        );
    };

    // Handle moving to the next question
    const handleNextQuestion = () => {
        // In a real app, you might send selectedAnswers to the backend here for immediate feedback/scoring
        // For this example, we just increment the index to get the next question.
        setQuestionIndex(prevIndex => prevIndex + 1);
    };

    // Handle test completion (e.g., final submit button)
    const handleSubmitTest = () => {
        // This will trigger the useEffect, which will then detect `isEndOfTest` from Lambda
        // and set `testCompleted` state.
        setQuestionIndex(prevIndex => prevIndex + 1); // Request next index to trigger end-of-test from Lambda
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                    Test Dashboard
                </h2>

                <div className="mb-4 text-center text-gray-700">
                    Welcome, <span className="font-semibold">{user?.email || 'Guest'}</span>!
                    <button
                        onClick={logout}
                        className="ml-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 ease-in-out shadow-sm"
                    >
                        Logout
                    </button>
                </div>

                {isLoading && (
                    <div className="text-center text-blue-600 text-lg mt-8">
                        <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-2">{message}</p>
                    </div>
                )}

                {!isLoading && message && !testCompleted && (
                    <p className="text-red-500 text-center text-lg mt-8">{message}</p>
                )}

                {testCompleted && (
                    <div className="text-center mt-8 p-6 bg-green-100 rounded-lg border border-green-300">
                        <h3 className="text-2xl font-semibold text-green-700 mb-4">
                            Congratulations!
                        </h3>
                        <p className="text-lg text-green-800">
                            You have completed the test. Your score is being calculated.
                            <br />
                            This is where your final score would be displayed.
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')} // Go back to dashboard or home
                            className="mt-6 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-200 ease-in-out shadow-md"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                )}

                {!isLoading && currentQuestion && !testCompleted && (
                    <div className="mt-8">
                        {currentQuestion.questionParagraph && (
                            <p className="text-gray-600 mb-4 text-justify leading-relaxed">
                                {currentQuestion.questionParagraph}
                            </p>
                        )}
                        <h3 className="text-xl font-semibold text-gray-800 mb-6">
                            {currentQuestion.questionIndex + 1}. {currentQuestion.questionText}
                        </h3>

                        <div className="space-y-4">
                            {currentQuestion.choices.map((choice) => (
                                <label key={choice.id} className="flex items-center space-x-3 bg-gray-50 p-4 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer transition-all duration-200 ease-in-out">
                                    {currentQuestion.questionType === "single-choice" ? (
                                        <input
                                            type="radio"
                                            name="answer"
                                            value={choice.id}
                                            checked={selectedAnswers.includes(choice.id)}
                                            onChange={handleSingleChoiceChange}
                                            className="form-radio h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                    ) : (
                                        <input
                                            type="checkbox"
                                            name="answer"
                                            value={choice.id}
                                            checked={selectedAnswers.includes(choice.id)}
                                            onChange={handleMultipleChoiceChange}
                                            className="form-checkbox h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                    )}
                                    <span className="text-gray-700 text-lg">{choice.text}</span>
                                </label>
                            ))}
                        </div>

                        <div className="mt-8 flex justify-end">
                            {!currentQuestion.isLastQuestion ? (
                                <button
                                    onClick={handleNextQuestion}
                                    disabled={selectedAnswers.length === 0} // Disable if no answer selected
                                    className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition duration-200 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next Question
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmitTest}
                                    disabled={selectedAnswers.length === 0} // Disable if no answer selected
                                    className="px-6 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition duration-200 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Submit Test
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TestScreen;
